import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import { useAppDispatch, useAppSelector } from '../../../../store/redux/hooks';
import {
  selectPass,
  initialiseFromPass,
  resetPass,
  syncFromTemplate,
} from '../../../../store/redux/slices/pass.slice';
import type { PassItem, PassBarcode, PassContent } from '../../../../store/types/pass';
import type { FieldLocalisations } from '../../../../store/redux/slices/pass.slice';
import type { AppleEnhancedDesignFeaturedActions } from '../../../../store/types/template';
import { mapFeaturedActionsFromApi } from '../../pass-templates-new/utils/mapApiResponseToTemplateState';
import { getStoredFeaturedActions } from '../utils/buildPassEnhancedPreview';

const deriveNfcSource = (
  nfcRaw: unknown,
  passId?: string,
  extId?: string
): string => {
  const nfc = nfcRaw as string | null;
  if (!nfc) return 'uuid';
  if (nfc === extId) return 'extId';
  if (nfc === passId) return 'uuid';
  return 'custom';
};

const deriveNfcCustomValue = (
  nfcRaw: unknown,
  passId?: string,
  extId?: string
): string => {
  const nfc = nfcRaw as string | null;
  if (!nfc || nfc === passId || nfc === extId) return '';
  return nfc;
};

const parseExpiryDate = (expiresAt?: string | null, tz?: string): string => {
  if (!expiresAt) return '';
  return moment.utc(expiresAt).tz(tz || 'UTC').format('YYYY-MM-DD');
};

const parseExpiryTime = (expiresAt?: string | null, tz?: string): string => {
  if (!expiresAt) return '';
  return moment.utc(expiresAt).tz(tz || 'UTC').format('HH:mm');
};

const deriveFieldLocalisations = (
  passContent?: PassContent
): FieldLocalisations => {
  const result: FieldLocalisations = {};
  if (!passContent) return result;
  Object.entries(passContent).forEach(([fieldId, value]) => {
    if (!value || typeof value !== 'object') return;
    if (!('localisation' in value) || !value.localisation) return;

    const locales: Record<string, string> = {};
    Object.entries(value.localisation).forEach(([locale, locValue]) => {
      if (typeof locValue === 'object' && locValue && 'value' in locValue) {
        locales[locale] = (locValue as { value: string }).value || '';
      } else if (typeof locValue === 'string') {
        locales[locale] = locValue;
      }
    });
    if (Object.keys(locales).length > 0) {
      result[fieldId] = locales;
    }
  });
  return result;
};

export const usePass = (pass?: PassItem) => {
  const dispatch = useAppDispatch();
  const passState = useAppSelector(selectPass);
  const location = useLocation();

  useEffect(() => {
    dispatch(resetPass());
  }, [location.key, dispatch]);

  useEffect(() => {
    if (!pass) return;
    const localTz = moment.tz.guess();
    dispatch(
      initialiseFromPass({
        fieldValues: {},
        extId: pass.attributes.extId || '',
        groupTags: pass.attributes.groupTags ?? [],
        barcode: pass.attributes.barcode
          ? {
              ...pass.attributes.barcode,
              value:
                pass.attributes.barcode.source === 'custom'
                  ? pass.attributes.barcode.value
                  : '',
            }
          : {
              enabled: false,
              type: null,
              source: null,
              value: '',
              displayText: false,
            },
        nfcEnabled: !!pass.attributes.nfc,
        nfcSource: deriveNfcSource(
          pass.attributes.nfc,
          pass.id,
          pass.attributes.extId
        ),
        nfcCustomValue: deriveNfcCustomValue(
          pass.attributes.nfc,
          pass.id,
          pass.attributes.extId
        ),
        expiryDate: parseExpiryDate(pass.attributes.expiresAt, localTz),
        expiryTime: parseExpiryTime(pass.attributes.expiresAt, localTz),
        expiryTimezone: localTz,
        templateUuid: '',
        fieldLocalisations: deriveFieldLocalisations(pass.attributes.passContent),
        featuredActions: mapFeaturedActionsFromApi(
          getStoredFeaturedActions(pass)
        ),
      })
    );
  }, [pass, dispatch]);

  const handleSyncFromTemplate = useCallback(
    (data: {
      barcode: PassBarcode;
      nfcEnabled: boolean;
      nfcSource: string;
      nfcCustomValue: string;
      featuredActions?: AppleEnhancedDesignFeaturedActions;
    }) => dispatch(syncFromTemplate(data)),
    [dispatch]
  );

  return {
    passState,
    handleSyncFromTemplate,
  };
};
